import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MinusIcon, PlusIcon, XIcon } from 'lucide-react';
import { Button } from './Button';
import { ProductVisual } from './ProductVisual';
import { useCart } from '../contexts/CartContext';
import { getProductPrice, products } from '../data/products';
import { formatPrice } from '../utils/currency';
export function CartDrawer() {
  const {
    lines,
    isOpen,
    close,
    updateQty,
    removeItem
  } = useCart();
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close]);
  const items = lines.map(l => ({
    line: l,
    product: products.find(p => p.id === l.productId)
  })).filter((x): x is {
    line: typeof x.line;
    product: NonNullable<typeof x.product>;
  } => Boolean(x.product));
  const subtotal = items.reduce((s, {
    line,
    product
  }) => s + getProductPrice(product, line.option) * line.qty, 0);
  return <AnimatePresence data-id="element-84" data-mp="cw1cnmwk6sp7">
      {isOpen && <div className="fixed inset-0 z-50" data-id="element-85" data-mp="cwudk78u6nzf">
          <motion.button type="button" aria-label="Close cart" onClick={close} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-charcoal/30" data-id="element-86" data-mp="841bjr4r5tr1" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Your cart" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-0 flex h-full w-full max-w-[30rem] flex-col bg-white" data-id="element-87" data-mp="vt2ljit8hcyf">
            <div className="flex items-center justify-between border-b border-stone px-6 py-5" data-id="element-88" data-mp="wktz70rj51rj">
              <h2 className="t-label text-charcoal" data-id="element-89" data-mp="11ilzai2bvu6t">Your Cart ({lines.reduce((s, l) => s + l.qty, 0)})</h2>
              <button type="button" onClick={close} aria-label="Close cart" className="grid h-10 w-10 place-items-center text-charcoal" autoFocus data-id="element-90" data-mp="16fvopxppw5un">
                <XIcon aria-hidden="true" strokeWidth={1.25} className="h-5 w-5" data-id="element-91" data-mp="1fier6gplcggd" />
              </button>
            </div>

            {items.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-92" data-mp="1kevbezto6ao6">
                <p className="t-h3" data-id="element-93" data-mp="1p9rfiyc6kohn">Your cart is empty.</p>
                <p className="mt-2 text-graphite" data-id="element-94" data-mp="1u5q00aue1mcs">Objects for every hour, waiting in the shop.</p>
                <Button to="/shop" className="mt-8" onClick={close} data-id="element-95" data-mp="1z6vt49rynbmu">
                  Explore the Shop
                </Button>
              </div> : <>
                <ul className="flex-1 divide-y divide-stone overflow-y-auto px-6" data-id="element-96" data-mp="3npqkc0zkhppg">
                  {items.map(({
              line,
              product
            }) => <li key={line.key} className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-4 py-5" data-id="element-97" data-mp="3jmli7zjefq1c">
                      <ProductVisual product={product} className="aspect-square" data-id="element-98" data-mp="3ep25j42cnlbi" />
                      <div className="flex flex-col" data-id="element-99" data-mp="39ra33g42wgt2">
                        <div className="flex items-start justify-between gap-3" data-id="element-100" data-mp="b7tq0qv3gxjy">
                          <div data-id="element-101" data-mp="b9whzgft50rb">
                            <p className="t-label text-[0.7rem] text-charcoal" data-id="element-102" data-mp="68qj10vk0h9c">{product.name}</p>
                            {line.option && <p className="mt-1 text-sm text-graphite" data-id="element-103" data-mp="1gfhxup2upf7">
                                {product.option?.label}: {line.option}
                              </p>}
                          </div>
                          <p className="text-sm text-charcoal" data-id="element-104" data-mp="p84fpqko463j">{formatPrice(getProductPrice(product, line.option) * line.qty)}</p>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-3" data-id="element-105" data-mp="9ojsqgzzcjb5">
                          <div className="flex items-center border border-[#8F877F]" data-id="element-106" data-mp="dszr7x79kogc">
                            <button type="button" onClick={() => updateQty(line.key, line.qty - 1)} aria-label={`Decrease quantity of ${product.name}`} className="grid h-9 w-9 place-items-center" data-id="element-107" data-mp="3vycsjn8vc3uw">
                              <MinusIcon aria-hidden="true" strokeWidth={1.25} className="h-3.5 w-3.5" data-id="element-108" data-mp="shnlm05dis4p" />
                            </button>
                            <span className="w-8 text-center text-sm" aria-live="polite" data-id="element-109" data-mp="xh8oumrktmn8">
                              {line.qty}
                            </span>
                            <button type="button" onClick={() => updateQty(line.key, line.qty + 1)} aria-label={`Increase quantity of ${product.name}`} className="grid h-9 w-9 place-items-center" data-id="element-110" data-mp="no1kn58kocoq">
                              <PlusIcon aria-hidden="true" strokeWidth={1.25} className="h-3.5 w-3.5" data-id="element-111" data-mp="1g7hwic64u6mb" />
                            </button>
                          </div>
                          <button type="button" onClick={() => removeItem(line.key)} className="text-sm text-graphite underline underline-offset-4 hover:text-charcoal" data-id="element-112" data-mp="2vy0z8n26r7bl">
                            Remove
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>
                <div className="border-t border-stone px-6 py-6" data-id="element-113" data-mp="2hzb0l2zpwzl4">
                  <Link to="/readings?type=birth-chart#book" onClick={close} className="mb-5 flex items-center justify-between gap-4 bg-cream px-4 py-3 text-sm text-charcoal hover:bg-stone" data-id="element-114" data-mp="2czy6bx6ioq8i">
                    <span data-id="element-115" data-mp="2lya6apt9x0dm">Pair it with a Birth Chart Reading</span>
                    <span className="t-label text-[0.6rem]" data-id="element-116" data-mp="67e3s1w4ezji">Book →</span>
                  </Link>
                  <div className="flex items-baseline justify-between" data-id="element-117" data-mp="19cako4zdjub">
                    <span className="t-label text-charcoal" data-id="element-118" data-mp="1b6uq08fh7xc">Subtotal</span>
                    <span className="text-lg text-charcoal" data-id="element-119" data-mp="2v0j1ksq3gh4u">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-sm text-graphite" data-id="element-120" data-mp="301zzqgh71phq">Shipping and taxes calculated at checkout.</p>
                  <Button to="/checkout" size="lg" className="mt-5 w-full" onClick={close} data-id="element-121" data-mp="33zo0i05kichj">
                    Checkout
                  </Button>
                </div>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>;
}